import React, { useState, useEffect, useMemo, useRef } from 'react';
import { DataGridPro, GridColDef, GridToolbar, GridRenderCellParams, DataGridProProps  } from '@mui/x-data-grid-pro';
import { useTheme, Box, Typography, Button, CircularProgress, Tabs, Tab, Alert, Stack, IconButton, Table, TableBody, TableCell, TableContainer, TableRow, TableHead, Dialog, DialogTitle, DialogContent, DialogActions, MobileStepper, Fade } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { ExpandMore as ExpandMoreIcon, ContentCopy as ContentCopyIcon, KeyboardArrowLeft, KeyboardArrowRight, ChevronLeft, ChevronRight } from '@mui/icons-material';         
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { useAmaS3Manager } from 'features/ama_v2/hooks/useAmaS3Manager';
import { FileStatus } from '../../../../data/enums';
import { alpha, styled } from '@mui/material/styles';
import { AmaV2Client, AmaV2DQReport, AmaV2DQRuleSummary, AmaV2Snapshot, AmaV2LogsReport, AmaV2DateLimiters, DateLimiterRow, DateLimiterColumn } from 'context/ama-v2-provider/ama-v2.types';
import { useAmaV2Context } from '../../../../context/ama-v2-provider';
import { AmaV2DescStats, AmaV2DescStatsSection, AmaV2DescStatsRow, AmaV2ActiveSnapshot } from '../../../../context/ama-v2-provider/ama-v2.types';
import { Bar, Line, Pie } from 'react-chartjs-2';
import 'chart.js/auto';

const DATE_LIMITER_CALC_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: number;
  value: number;
}

interface DataQualityReportProps {
  snapshot: AmaV2Snapshot; 
  closeModal: () => void;
  initialTab?: number;
};

export default function SnapshotDataQualityReport(props: DataQualityReportProps) {

  const theme = useTheme();
  const [ logReports, setLogReports ] = useState<Array<{ name: string; logEntries: string[]; generated_at?: string; count?: number }> | null>(null);  
  const [ isDataQualityDownloading, setIsDataQualityDownloading ] = useState<boolean>(false);  
  const [ isLogsDownloading, setIsLogsDownloading ] = useState<boolean>(false);
  const [ isChartDataDownloading, setIsChartDataDownloading ] = useState<boolean>(false);
  const [ dqReport, setDqReport ] = useState<AmaV2DQReport | null>(null);
  const [ chartData, setChartData ] = useState<any | null>(null);
  const [ warnings, setWarnings ] = useState<Array<{ id: number; type: 'warning' | 'error'; message: string }> | null>(null);
  const [expanded, setExpanded] = React.useState<string | false>('panel0');
  const [tabValue, setTabValue] = React.useState(props.initialTab ?? 0);
  const { dateLimitersState, loadDescStats, loadDateLimiters, clearDateLimiters, updateDateLimiters, get_active_snapshot, activeSnapshotPollIntervalMs } = useAmaV2Context();
  const [activeSnapshot, setActiveSnapshot] = useState<AmaV2ActiveSnapshot | null>(null);
  const [limitersLocal, setLimitersLocal] = useState<AmaV2DateLimiters | null>(null);
  const [editingBuffer, setEditingBuffer] = useState<Record<string, string>>({});
  const [limitersDefault, setLimitersDefault] = useState<AmaV2DateLimiters | null>(null);
  const [yearEditBuffer, setYearEditBuffer] = useState<Record<string, string>>({});
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [processingMessage, setProcessingMessage] = useState<string | null>(null);
  const [errorOpen, setErrorOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const dateLimitersScrollRef = useRef<HTMLDivElement>(null);
  const [activeCarouselStep, setActiveCarouselStep] = useState(0);
  const [chartAccordionState, setChartAccordionState] = useState<Record<string, boolean>>({});
  const [thresholdValues, setThresholdValues] = useState<Record<string, number>>({});
  const [thresholdInputBuffer, setThresholdInputBuffer] = useState<Record<string, string>>({});
  const thresholdInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const scrollPositionRef = useRef<number>(0);
  

  
  const { isS3initialized, getObjectsForBucket, downloadJsonFile } = useAmaS3Manager({ 
    bucketName: process.env.REACT_APP_AWS_V2_S3_DATA_LAKE_BUCKET!,
    clientId: props.snapshot.client_id,
    academicYear: props.snapshot.academic_year,
    snapshotDate: props.snapshot.snapshot_date,
    existingFileKey: props.snapshot.bronze_path || props.snapshot.ingest_path || null,
    existingStatus: props.snapshot.bronze_path ? FileStatus.Uploaded : undefined,
  });

  const getLineChartCofigForMetric = (id:string,label:string, metric:string,text:string) => {
     return {
        id,
        label,
        chartType: 'line' as const,
        data: {
          labels: chartData.labels,
          datasets: chartData.datasets
            .filter((ds: any) => ds.metric === metric)
            .map((ds: any, idx: number) => {
              const colors = [
                theme.palette.primary.main,
                theme.palette.secondary.main,
                theme.palette.warning.main,
                theme.palette.error.main,
                theme.palette.info.main,
              ];
              return {
                label: ds.label,
                data: ds.data,
                borderColor: colors[idx % colors.length],
                backgroundColor: colors[idx % colors.length],
                borderWidth: 2,
                tension: 0.1,
                pointRadius: 0,
                pointHoverRadius: 4,
              };
            })
            .reverse(),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false as const,
          plugins: {
            legend: { 
              position: 'bottom' as const,
              labels: {
                boxWidth: 30,
                boxHeight: 8,
                usePointStyle: false,
              }
            },
            tooltip: { mode: 'index' as const, intersect: false },
            title: { 
              display: true,
              text,
              position: 'top' as const,
              color: theme.palette.text.primary,
              font: {
                size: 14,
                weight: 'bold' as const,
                family: theme.typography.fontFamily,
              },
            },
          },
          interaction: { mode: 'nearest' as const, intersect: false },
          scales: {
            x: {
              ticks: { 
                color: theme.palette.text.secondary,
                maxRotation: 45,
                minRotation: 45,
              },
              grid: { display: false },
            },
            y: {
              beginAtZero: true,
              ticks: { color: theme.palette.text.secondary },
              grid: { color: alpha(theme.palette.divider, 0.3) },
            },
          },
        },
      }
  }

  useEffect(() => {
    
    // First, we need to get active snapshot. If it does NOT match current snapshot then date limiters and desc stats are not relevant.
    fetchActiveSnapshot(props.snapshot.client_id);

    // Clear API-derived state and local buffers when this modal unmounts
    return () => {
      setActiveSnapshot(null);
      clearDateLimiters(); 
      setDqReport(null);
      setLogReports(null);
      setLimitersLocal(null);
      setLimitersDefault(null);
      setEditingBuffer({});
    };
  }, []);

  useEffect(() => { 

    if (!isS3initialized) return;

    // Keys for artifacts
    const dqrKey = `data/silver/${props.snapshot.client_id}/academic_year=${props.snapshot.academic_year}/snapshot_date=${props.snapshot.snapshot_date}/dq/dq_report.json`;
    const logsKey = `data/silver/${props.snapshot.client_id}/academic_year=${props.snapshot.academic_year}/snapshot_date=${props.snapshot.snapshot_date}/logs/logs.json`;
    const chartDataKey = `data/silver/${props.snapshot.client_id}/academic_year=${props.snapshot.academic_year}/snapshot_date=${props.snapshot.snapshot_date}/date_limiter_vis/date_limiter_vis_data.json`;

    console.log('[SnapshotDataQualityReport] Fetching artifacts:', { dqrKey, logsKey, chartDataKey });

    // Helper to parse JSON safely
    const safeParse = <T,>(raw: string | null, label: string, key: string): T | null => {
      if (!raw) {
        // File doesn't exist - this is normal for unprocessed snapshots
        return null;
      }
      try {
        return JSON.parse(raw) as T;
      } catch (e) {
        console.error(`[SnapshotDataQualityReport] Failed parsing ${label} JSON at ${key}`, e);
        return null;
      }
    };

    const fetchDQ = async () => {
      setIsDataQualityDownloading(true);
      try {
        const dqRaw = await downloadJsonFile(dqrKey);
        const dqParsed = safeParse<AmaV2DQReport>(dqRaw, 'DQ report', dqrKey);
        if (dqParsed) {
          if ((dqParsed as any).value_rule_exception_sample) {
            const exceptionRules = (dqParsed as any).value_rule_exception_sample.rules;
            dqParsed.rule_summaries.forEach(summary => {
              const matchingRule = exceptionRules.find((r: any) => r.attribute === summary.attribute && r.rule_index === summary.rule_index);
              if (matchingRule) {
                summary.samples = matchingRule.samples;
              }
            });
          }
          setDqReport(dqParsed);
          console.log('[SnapshotDataQualityReport] DQ report parsed. Rules:', dqParsed.rule_summaries.length);
        } else {
          setDqReport(null);
        }
      } catch (err) {
        console.error('[SnapshotDataQualityReport] Error downloading DQ report', err);
        setDqReport(null);
      } finally {
        setIsDataQualityDownloading(false);
      }
    };

    const fetchLogs = async () => {
      setIsLogsDownloading(true);
      try {
        const logsRaw = await downloadJsonFile(logsKey);
        const logsParsed = safeParse<AmaV2LogsReport>(logsRaw, 'Logs report', logsKey);
        if (logsParsed) {
          setLogReports([
            {
              name: 'Silver Logs',
              logEntries: logsParsed.lines,
              generated_at: logsParsed.generated_at,
              count: logsParsed.count,
            },
          ]);
          console.log('[SnapshotDataQualityReport] Logs report parsed. Lines:', logsParsed.count);
        } else {
          setLogReports([]);
        }
      } catch (err) {
        console.error('[SnapshotDataQualityReport] Error downloading logs', err);
        setLogReports(null);
      } finally {
        setIsLogsDownloading(false);
      }
    };

    const fetchChartData = async () => {
      setIsChartDataDownloading(true);
      try {
        const chartDataRaw = await downloadJsonFile(chartDataKey);
        // console.log('[SnapshotDataQualityReport] Chart data raw:', chartDataRaw?.substring(0, 500));
        const chartDataParsed = safeParse<any>(chartDataRaw, 'Chart data', chartDataKey);
        if (chartDataParsed) {
          // console.log('[SnapshotDataQualityReport] Chart data parsed successfully');
          // console.log('[SnapshotDataQualityReport] Chart data structure:', {
          //   hasLabels: !!chartDataParsed.labels,
          //   labelsCount: chartDataParsed.labels?.length,
          //   hasDatasets: !!chartDataParsed.datasets,
          //   datasetsCount: chartDataParsed.datasets?.length,
          //   datasetMetrics: chartDataParsed.datasets?.map((ds: any) => ds.metric),
          //   sampleDataset: chartDataParsed.datasets?.[0]
          // });
          console.log("Setting up chart data");
          console.log(chartDataParsed);
          setChartData(chartDataParsed);
        } else {
          // console.log('[SnapshotDataQualityReport] Chart data parse returned null');
          setChartData(null);
        }
      } catch (err) {
        console.error('[SnapshotDataQualityReport] Error downloading chart data', err);
        setChartData(null);
      } finally {
        setIsChartDataDownloading(false);
      }
    };

    // Fire all requests concurrently
    fetchDQ();
    fetchLogs();
    fetchChartData();

  }, [isS3initialized, props.snapshot]);

  useEffect(() => {

    const snap = props.snapshot;
    if (!snap) { setWarnings(null); return; }

    const normalize = (raw: string[] | string | undefined): string[] => {
      if (!raw) return [];
      if (Array.isArray(raw)) return raw.filter(r => r && r.trim().length > 0);
      if (typeof raw === 'string') return raw.trim().length ? [raw] : [];
      return [];
    };

    const wList = normalize(snap.warnings);
    const eList = normalize(snap.errors);
    let idCounter = 1;
    const combined: Array<{ id: number; type: 'warning' | 'error'; message: string }> = [
      ...wList.map(msg => ({ id: idCounter++, type: 'warning' as const, message: msg })),
      ...eList.map(msg => ({ id: idCounter++, type: 'error' as const, message: msg }))
    ];
    setWarnings(combined);
  }, [props.snapshot]);
  
  useEffect(() => {
    
    // Initialize date limiters local and default copies when provider data loads
    if (dateLimitersState.status === 'success' && dateLimitersState.data) {
      const src = dateLimitersState.data;
      const def: AmaV2DateLimiters = {
        years: [...(src.years ?? [])],
        columns: [...(src.columns ?? [])],
        rows: (src.rows ?? []).map(r => ({ year: r.year, dates: { ...(r.dates ?? {}) } }))
      };
      setLimitersDefault(def);
      setLimitersLocal({
        years: [...def.years],
        columns: [...def.columns],
        rows: def.rows.map(r => ({ year: r.year, dates: { ...(r.dates ?? {}) } }))
      });
      setEditingBuffer({});
    }
  }, [dateLimitersState.status, dateLimitersState.data]);
    
  useEffect(() => {
    
    // Preserve scroll position when carousel step, threshold values, or accordion state changes
    const scrollContainer = dateLimitersScrollRef.current;
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollPositionRef.current;
    }
  }, [activeCarouselStep, thresholdValues, chartAccordionState]);

  const chartConfigs = useMemo(() => {

    if (!chartData) return [];

    const configs = [
      // Chart 1: Apps - Daily Application Trends by Year
      getLineChartCofigForMetric('apps-daily-trends', 'Apps', 'Apps','Daily Application Trends by Year'),
      // Chart 2: Admits - Daily Admission Trends by Year
      getLineChartCofigForMetric('admits-daily-trends', 'Admits', 'Admits','Daily Admission Trends by Year'),
      // Chart 3: FAFSA - Daily FAFSA Submission Trends by Year
      getLineChartCofigForMetric('fafsa-daily-trends','FAFSA','FAFSA', 'Daily FAFSA Submission Trends by Year'),
      // Chart 4: CSS - Daily CSS Profile Trends by Year
      getLineChartCofigForMetric('css-daily-trends','CSS','CSS','Daily CSS Profile Trends by Year'),
      // Chart 5: Financial Aid - Daily Finandcial aid Profile Trends by Year
      getLineChartCofigForMetric('financial-aid-daily-trends','Financial Aid','Financial Aid','Daily Financial Aid Profile Trends by Year'),
      // Chart 6: Deposit - Daily Deposit Trends by Year
      getLineChartCofigForMetric('deposit-daily-trends', 'Deposit', 'Deposits', 'Daily Deposit Trends by Year'),
      // Chart 7: Visit - Daily Campus Visit Trends by Year
      getLineChartCofigForMetric('visit-daily-trends', 'Visit', 'Visits', 'Daily Campus Visit Trends by Year'),
      // Chart 8: Withdrawn - Daily Withdrawal Trends by Year
      getLineChartCofigForMetric('withdrawn-daily-trends', 'Withdrawn', 'Withdrawn', 'Daily Withdrawal Trends by Year')
    ];

    return configs;
  }, [chartData, theme]);

  const fetchActiveSnapshot = async (client_id: string) => {

    try {

      const activeSnapshot = await get_active_snapshot(client_id);
      setActiveSnapshot(activeSnapshot);

      if (activeSnapshot?.client_id === props.snapshot.client_id && 
        activeSnapshot.academic_year ===  props.snapshot.academic_year &&
        activeSnapshot.snapshot_date === props.snapshot.snapshot_date) {

        // Desc stats has been cached (based on most recent calculations)
        setActiveSnapshot(activeSnapshot);

        // Load date limiters for active snapshot
        if (!activeSnapshot.is_processing) {
          loadDateLimiters(activeSnapshot.client_id, activeSnapshot.academic_year, activeSnapshot.snapshot_date);  
        } else {
          setTabValue(1);
        }
      } else {
        setTabValue(1);
      }
    } catch (err) {

      setActiveSnapshot(null);

    } finally {
      // TODO
    }
  };

  const hasLimiterChanges = (curr: AmaV2DateLimiters | null, def: AmaV2DateLimiters | null): boolean => {
    if (!curr || !def) return false;
    for (const row of curr.rows ?? []) {
      const defRow = (def.rows ?? []).find(r => r.year === row.year);
      for (const col of (curr.columns ?? [])) {
        const c = row.dates?.[col.id] || '';
        const d = defRow?.dates?.[col.id] || '';
        if (c !== d) return true;
      }
    }
    return false;
  };

  const getDetailPanelContent = React.useCallback<NonNullable<DataGridProProps['getDetailPanelContent']>>(({ row }) => {
    if (row.fail_count <= 0) return null;
    return <DetailPanelContent row={row} />;
  }, []);

  const getDetailPanelHeight = React.useCallback(() => 'auto', []);

  const a11yProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  };  

  const CustomTabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;  
    return (
      <Box
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
        sx={{ width: '100%', position: 'relative', height: '100%', flex: '1', }}>

        {value === index && (
          <Box sx={{ height: '100%', width: '100%', p: 0 }}>
            {children}
          </Box>
        )}
      </Box>
    );
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (isProcessing) return;
    setTabValue(newValue);
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  const cellStyle = {
    fontSize: '12px',
    overflowX: 'hidden',
    border: `solid 1px ${theme.palette.grey[100]}`
  };

  const formatTimestampMinutePrecision = (isoString: string): string => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return isoString;

      const formatted = new Intl.DateTimeFormat(undefined, {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
        timeZoneName: 'short'
      }).format(date);

      // Ensure consistent spacing and remove comma before time component if locale inserts one.
      return formatted.replace(/,\s(\d{2}:\d{2})/, ' $1');
    } catch {
      return isoString;
    }
  };

  const CustomFooter = (count: number | undefined) => {
    return (
      <Stack sx={{ height: 30, backgroundColor: 'white' }} justifyContent="center" alignItems="center">
        Records: {count}
      </Stack>
    );
  };

  const handleDateChange = (year: string, columnId: string, value: Dayjs | null) => {
    const key = `${year}|${columnId}`;
    if (!value || !value.isValid()) {
      // Ignore invalid interim input to prevent re-render/focus loss while typing
      return;
    }
    // Preserve scroll position of the Date Limiters container
    const prevScrollTop = dateLimitersScrollRef.current?.scrollTop ?? 0;
    const iso = value.format('YYYY-MM-DD');
    setEditingBuffer(prev => ({ ...prev, [key]: iso }));
    setLimitersLocal((prev: AmaV2DateLimiters | null) => {
      if (!prev) return prev;
      const next: AmaV2DateLimiters = { ...prev, rows: prev.rows.map((r: DateLimiterRow) => ({ ...r, dates: { ...(r.dates ?? {}) } })) };
      const rowIdx = next.rows.findIndex((r: DateLimiterRow) => r.year === year);
      if (rowIdx >= 0) {
        next.rows[rowIdx].dates[columnId] = iso.replace(/-/g, '');
      }
      return next;
    });
    // Restore scroll position after state update
    requestAnimationFrame(() => {
      if (dateLimitersScrollRef.current) {
        dateLimitersScrollRef.current.scrollTop = prevScrollTop;
      }
    });
  };

  const handleDateBlur = (year: string, columnId: string) => {
    const key = `${year}|${columnId}`;
    const val = editingBuffer[key];
    if (!val || !/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      setEditingBuffer(prev => { const next = { ...prev }; delete next[key]; return next; });
    }
  };

  const commitIfValidInput = (year: string, columnId: string, raw: string) => {
    const parsed = dayjs(raw, 'YYYY-MM-DD', true);
    if (parsed.isValid()) {
      handleDateChange(year, columnId, parsed);
    }
  };

  const resetLimiters = () => {
    if (limitersDefault) {
      setLimitersLocal({
        years: [...(limitersDefault.years ?? [])],
        columns: [...(limitersDefault.columns ?? [])],
        rows: (limitersDefault.rows ?? []).map(r => ({ year: r.year, dates: { ...(r.dates ?? {}) } }))
      });
      setEditingBuffer({});
    }
  };

  const handleSaveAndRecalculate = async () => {

    if (!limitersLocal || !props.snapshot) return;

    try {

      setIsProcessing(true);
      setProcessingMessage('We are saving date limiters and recalculating descriptive statistics. This could take several minutes, please wait');

      // Kick off async backend process; expect 202 Accepted
      const ok = await updateDateLimiters(
        props.snapshot.client_id,
        props.snapshot.academic_year,
        props.snapshot.snapshot_date,
        limitersLocal
      );
      if (!ok) {
        throw new Error('Save or recalc request failed');
      }

      // Poll active snapshot until processing completes (or timeout)
      const pollInterval = activeSnapshotPollIntervalMs ?? 3000;
      const start = Date.now();
      while (Date.now() - start < DATE_LIMITER_CALC_TIMEOUT_MS) {
        const activeSnapshot = await get_active_snapshot(props.snapshot.client_id);
        console.log(`Polling active snapshot. is_processing: ${activeSnapshot?.is_processing}, desc_stats: ${activeSnapshot?.desc_stats ? 'present' : 'null'} `);
        if (activeSnapshot && activeSnapshot.is_processing === false) {
          // setDescStats(activeSnapshot.desc_stats || null);
          setActiveSnapshot(activeSnapshot);
          break;
        }
        await new Promise(r => setTimeout(r, pollInterval));
      }

      if (Date.now() - start >= DATE_LIMITER_CALC_TIMEOUT_MS) {
        throw new Error('Recalculation timed out after 5 minutes.');
      }

      // Refresh data once processing completes
      const snap = props.snapshot;
      await Promise.all([
        // loadDescStats(snap.client_id, snap.academic_year, snap.snapshot_date),
        loadDateLimiters(snap.client_id, snap.academic_year, snap.snapshot_date)
      ]);

      setIsProcessing(false);
      setProcessingMessage(null);
    } catch (e: any) {
      setIsProcessing(false);
      setProcessingMessage(null);
      setErrorMessage(e?.message || 'An error occurred while saving date limiters and waiting for recalculation to complete.');
      setErrorOpen(true);
    }
  };

  const RenderCopyColumn = (row: any) => {
    
    const clickHandler = async () => {
      navigator.clipboard.writeText(JSON.stringify(row));
    };

    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end'}}>
        <IconButton 
          aria-label="Copy row to clipboard."
          sx={{ color: theme.palette.grey[500]}}
          onClick={clickHandler}>
          <ContentCopyIcon sx={{ fontSize: 'medium' }}/>
        </IconButton>
      </Box>
    );    
  }

  const dataQualityColumns: Array<GridColDef> = [  
    { 
      field: 'attribute', type: 'string',
      headerName: 'Attribute', flex: .2, minWidth: 120,
      resizable: true, sortable: true
    },
    { 
      field: 'pass_pct', type: 'integer',
      headerName: 'Pass %', flex: .15, 
      resizable: true, sortable: true,
      valueFormatter: (params) => {
        const v = params.value;
        if (v === null || v === undefined || isNaN(Number(v))) return '';
        return `${Number(v).toFixed(1)}%`;
      }
    },  
    { 
      field: 'pass_count', type: 'integer',
      headerName: 'Pass', flex: .15, 
      resizable: true, sortable: true
    },
    { 
      field: 'fail_count', type: 'integer',
      headerName: 'Fail', flex: .15, 
      resizable: true, sortable: true
    },  
    { 
      field: 'expression', type: 'string',
      headerName: 'Rule', flex: 1, 
      resizable: true, sortable: true
    }, 
    { 
      field: 'copy', type: 'string', headerName: '',
      resizable: false, sortable: false, flex: 0, width: 50,
      renderCell: (params: GridRenderCellParams<any, Date>) => (<> { RenderCopyColumn(params.row as AmaV2DQRuleSummary) } </>)
    }
  ];  

  const logColumns: Array<GridColDef> = [  
    { 
      field: 'logEntry', type: 'string', headerName: 'Log Entry', 
      flex: 1, 
      resizable: true, sortable: true      
    },
    { 
      field: 'copy', type: 'string', headerName: '',
      resizable: false, sortable: false, flex: 0, width: 50,
      renderCell: (params: GridRenderCellParams<any, Date>) => (<> { RenderCopyColumn(params.row as string) } </>)
    }
  ];

  const warningsColumns: Array<GridColDef> = [
    {
      field: 'type', type: 'string', headerName: 'Type', flex: .15,
      resizable: true, sortable: true,
      valueGetter: (params) => params.row.type
    },
    {
      field: 'message', type: 'string', headerName: 'Message', flex: 1,
      resizable: true, sortable: true,
    },
    {
      field: 'copy', type: 'string', headerName: '',
      resizable: false, sortable: false, flex: 0, width: 50,
      renderCell: (params: GridRenderCellParams<any, Date>) => (<> { RenderCopyColumn(params.row as string) } </>)
    }
  ];

  const DetailPanelContent = ({ row }: { row: AmaV2DQRuleSummary }) => {
    return (
      <Box sx={{ width: '100%', bgcolor: theme.palette.grey[100]}}>
        <Box sx={{ pl: 6 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: '0.8rem' }}>PK</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>Value</TableCell>
              </TableRow>
            </TableHead>
          <TableBody>
            {row.samples?.map((sample, index) => (
              <TableRow key={index}>
                <TableCell sx={{ fontSize: '0.8rem' }}>{sample.pk}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{sample.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{
      position: 'absolute' as const,
      top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
      width: '80vw', height: '80vh',
      bgcolor: 'background.paper', border: '2px solid #000', boxShadow: 24,
      p: 4,
      display: 'flex', flexDirection: 'column',}}>

      {/* Modal Header */}
      <Box sx={{ flex: 'none', position: 'relative' }}>
        <Typography component="h2" variant="h6" color="primary" gutterBottom sx={{pb: 0}}>
          Data Quality and Logging
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ height: '100%', flex: '1', display: 'flex', flexDirection: 'column' }}>

        {/* Tabbed content */}
        <Box sx={{ height: 'auto', flex: 'none'}}>
          <Tabs value={tabValue} onChange={handleChange} aria-label="AMA tabbed features">
            <Tab label="Date Limiters" disabled={isProcessing} {...a11yProps(0)} />   
            <Tab label="Data Quality" disabled={isProcessing} {...a11yProps(1)} />
            <Tab label="Warnings/Errors" disabled={isProcessing} {...a11yProps(2)} />
            <Tab label="Logging" disabled={isProcessing} {...a11yProps(3)} />                     
          </Tabs>
        </Box>

        {/* Date Limiters Content Section */}
        <CustomTabPanel value={tabValue} index={0}>
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, p: 0 }}>

            {/* Loading interstitial */}
            {(dateLimitersState.status === 'loading' || isProcessing || !activeSnapshot || isChartDataDownloading) && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255,255,255,0.6)',
                  zIndex: 5,
                }}
              >
                <CircularProgress />
                {!!processingMessage && (
                  <Alert severity="info" sx={{ mt: 2, maxWidth: 720 }}>
                    {processingMessage}
                  </Alert>
                )}
              </Box>
            )}

            {/* Scrollable content container */}
            <Box ref={dateLimitersScrollRef} sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
            }}>

              {/* Descriptive Statistics table */}
              {activeSnapshot && activeSnapshot.desc_stats && dateLimitersState.status === 'success' && !isProcessing && (
                <Box sx={{ flex: 'none', p: 1, backgroundColor: 'white', overflowX: 'hidden' }}>
                  {(activeSnapshot.desc_stats.sections ?? []).map((section: AmaV2DescStatsSection, idx) => (
                    <Box key={section.id} sx={{ mb: 0.5 }}>
                      <Table sx={{ width: 'auto', tableLayout: 'auto', display: 'inline-table', mt: 2,
                        '& th, & td': { border: 'none', padding: '2px 6px', lineHeight: 1.1 },
                        '& thead th': { fontWeight: 600 },
                        '& th:not(:first-of-type), & td:not(:first-of-type)': { paddingLeft: '15px', minWidth: '90px'},
                        '& tbody .MuiTableRow-root': { height: 24, minHeight: 24 },
                      }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }} align="right">{section.label}</TableCell>
                            {(activeSnapshot.desc_stats!.years ?? []).map((yr) => (
                              <TableCell key={yr} sx={{ fontWeight: 600 }} align="right">{yr}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {section.rows.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell align="right">{row.label}</TableCell>
                              {(activeSnapshot.desc_stats?.years ?? []).map((yr) => {
                                const val = row.values[yr];
                                let display = '';
                                if (val === undefined || val === null || Number.isNaN(val)) {
                                  display = '';
                                } else if (row.type === 'percent') {
                                  display = `${(val * 100).toFixed(1)}%`;
                                } else if (row.type === 'currency') {
                                  display = new Intl.NumberFormat(undefined, { style: 'currency', currency: row.currency || 'USD', maximumFractionDigits: 0 }).format(val);
                                } else {
                                  display = new Intl.NumberFormat(undefined).format(val);
                                }
                                return (
                                  <TableCell key={`${row.id}-${yr}`} align="right">{display}</TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {idx < ((activeSnapshot.desc_stats!.sections ?? []).length - 1) && (
                        <Box sx={{ borderTop: '1px solid', borderColor: theme.palette.grey[200], mt: 1 }} />
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Carousel for multiple charts */}
              {activeSnapshot && activeSnapshot.desc_stats && dateLimitersState.status === 'success' && !isProcessing && (() => {
                // Show message if chart data not available
                if (!chartData || chartConfigs.length === 0) {
                  return (
                    <Box sx={{ flex: 'none', mt: 2, p: 2, bgcolor: 'white', border: theme => `1px solid ${theme.palette.grey[300]}`, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 100 }}>
                      <Alert severity='info'>Chart data not available for this snapshot.</Alert>
                    </Box>
                  );
                }

                const maxSteps = chartConfigs.length;

                const handleNext = () => {
                  if (dateLimitersScrollRef.current) {
                    scrollPositionRef.current = dateLimitersScrollRef.current.scrollTop;
                  }
                  setActiveCarouselStep((prevStep) => (prevStep + 1) % maxSteps);
                };

                const handleBack = () => {
                  if (dateLimitersScrollRef.current) {
                    scrollPositionRef.current = dateLimitersScrollRef.current.scrollTop;
                  }
                  setActiveCarouselStep((prevStep) => (prevStep - 1 + maxSteps) % maxSteps);
                };

                const currentChart = chartConfigs[activeCarouselStep];
                const chartId = currentChart.id;
                
                // Initialize accordion state for this chart if not set (default: expanded)
                const isAccordionOpen = chartAccordionState[chartId] ?? true;
                
                const toggleAccordion = () => {
                  // Save scroll position before state change
                  if (dateLimitersScrollRef.current) {
                    scrollPositionRef.current = dateLimitersScrollRef.current.scrollTop;
                  }
                  setChartAccordionState(prev => ({
                    ...prev,
                    [chartId]: !isAccordionOpen
                  }));
                };

                const currentThreshold = thresholdValues[chartId];

                const handleThresholdInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  // Only allow positive integers (0-9)
                  if (value !== '' && !/^[0-9]+$/.test(value)) {
                    e.preventDefault();
                    const inputEl = thresholdInputRefs.current[chartId];
                    if (inputEl) {
                      inputEl.value = inputEl.value.replace(/[^0-9]/g, '');
                    }
                  }
                };

                const applyThreshold = () => {
                  const inputEl = thresholdInputRefs.current[chartId];
                  if (!inputEl) return;
                  const value = inputEl.value.trim();
                  // Save scroll position before state change
                  if (dateLimitersScrollRef.current) {
                    scrollPositionRef.current = dateLimitersScrollRef.current.scrollTop;
                  }
                  if (value && /^[0-9]+$/.test(value)) {
                    const numValue = Number(value);
                    if (numValue >= 0) {
                      setThresholdValues(prev => ({ ...prev, [chartId]: numValue }));
                    }
                  } else if (value === '') {
                    // Clear threshold if input is empty
                    setThresholdValues(prev => {
                      const next = { ...prev };
                      delete next[chartId];
                      return next;
                    });
                  }
                };

                const handleThresholdKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    applyThreshold();
                  }
                };

                // Calculate threshold days (which datasets reach the threshold and when)
                const thresholdDays = (() => {
                  if (currentThreshold === undefined || !currentChart.data?.datasets) return [];
                  
                  const labels = currentChart.data.labels || [];
                  
                  return currentChart.data.datasets.map((ds: any) => {
                    const dayIndex = ds.data.findIndex((val: number) => val >= currentThreshold);
                    return {
                      label: ds.label,
                      day: dayIndex >= 0 ? labels[dayIndex] : 'Not reached',
                      value: dayIndex >= 0 ? ds.data[dayIndex] : null
                    };
                  });
                })();

                return (
                  <Box sx={{ 
                    flex: 'none', 
                    m: 0, 
                    p: 0, 
                    backgroundColor: 'white', 
                    border: theme => `1px solid ${theme.palette.grey[300]}`, 
                    borderRadius: 1, 
                    position: 'relative', 
                    overflow: 'hidden',
                    contain: 'layout style paint'
                  }} tabIndex={-1}>
                    {/* Navigation Buttons */}
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        (e.target as HTMLElement).blur();
                        handleBack();
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                      disableFocusRipple
                      disableRipple
                      sx={{
                        position: 'absolute',
                        left: 8,
                        bottom: 15,
                        zIndex: 1,
                        bgcolor: 'white',
                        '&:hover': { bgcolor: theme.palette.grey[100] },
                        boxShadow: 1
                      }}
                      tabIndex={-1}
                    >
                      <KeyboardArrowLeft />
                    </IconButton>
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        (e.target as HTMLElement).blur();
                        handleNext();
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                      disableFocusRipple
                      disableRipple
                      sx={{
                        position: 'absolute',
                        right: 8,
                        bottom: 15,
                        zIndex: 1,
                        bgcolor: 'white',
                        '&:hover': { bgcolor: theme.palette.grey[100] },
                        boxShadow: 1
                      }}
                      tabIndex={-1}
                    >
                      <KeyboardArrowRight />
                    </IconButton>

                    {/* Carousel Content */}
                    <Box sx={{ height: 300, display: 'flex', flexDirection: 'row', position: 'relative', m: 0, p: 0 }} tabIndex={-1}>
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', minWidth: 0, position: 'relative' }} tabIndex={-1}>
                          {/* Chart area - takes remaining space */}
                          <Box sx={{ flex: 1, position: 'relative', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }} tabIndex={-1}>
                            <Line data={currentChart.data} options={currentChart.options} />
                          </Box>
                          
                          {/* Vertical Accordion on the right */}
                          <Box sx={{ 
                            height: 'calc(100% + 40px)',
                            display: 'flex',
                            flexDirection: 'row',
                            position: 'relative'
                          }}>
                            {/* Vertical accordion toggle button */}
                            <Box sx={{
                              width: 16,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: theme.palette.grey[200],
                              borderLeft: `1px solid ${theme.palette.grey[300]}`,
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: theme.palette.grey[300]
                              },
                              transition: 'background-color 0.2s'
                            }}
                            onClick={toggleAccordion}
                            >
                              <IconButton size="small" sx={{ p: 0.5 }}>
                                {isAccordionOpen ? <ChevronRight /> : <ChevronLeft />}
                              </IconButton>
                            </Box>
                            
                            {/* Accordion content area */}
                            <Box sx={{
                              width: isAccordionOpen ? 250 : 0,
                              overflow: 'hidden',
                              transition: 'width 0.3s ease',
                              borderLeft: isAccordionOpen ? `1px solid ${theme.palette.grey[300]}` : 'none',
                              display: 'flex',
                              flexDirection: 'column',
                              bgcolor: theme.palette.grey[50]
                            }}>
                              {isAccordionOpen && (
                                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                  {/* Title/Label */}
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, px: 2, pt: 2 }}>
                                    Filter Threshold
                                  </Typography>
                                  
                                  {/* Textbox Entry and Threshold Value Row */}
                                  <Box sx={{ mb: 1.5, px: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                      ref={(el: HTMLInputElement | null) => {
                                        thresholdInputRefs.current[chartId] = el;
                                      }}
                                      component="input"
                                      type="text"
                                      placeholder="Enter threshold"
                                      defaultValue=""
                                      onChange={handleThresholdInputChange}
                                      onBlur={applyThreshold}
                                      onKeyDown={handleThresholdKeyDown}
                                      sx={{
                                        width: '66.67%',
                                        padding: '6px 8px',
                                        fontSize: '0.875rem',
                                        border: `1px solid ${theme.palette.grey[400]}`,
                                        borderRadius: '4px',
                                        outline: 'none',
                                        fontFamily: theme.typography.fontFamily,
                                        '&:focus': {
                                          borderColor: theme.palette.primary.main,
                                          boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
                                        }
                                      }}
                                    />
                                    {currentThreshold !== undefined && (
                                      <Box sx={{ flex: 1, textAlign: 'right', fontSize: '11px', color: theme.palette.grey[600], fontWeight: 600, pr: '10px' }}>
                                        {currentThreshold.toLocaleString()}
                                      </Box>
                                    )}
                                  </Box>
                                  
                                  {/* List of filtered items - takes remaining space */}
                                  <Box sx={{ px: 2, mb: 1 }}>
                                    {/* Static Header */}
                                    <Box sx={{
                                      border: `1px solid ${theme.palette.grey[300]}`,
                                      borderBottom: 'none',
                                      borderTopLeftRadius: '4px',
                                      borderTopRightRadius: '4px',
                                      bgcolor: theme.palette.grey[100],
                                      display: 'flex',
                                      padding: '6px 7px',
                                      lineHeight: 1.1,
                                      fontSize: '0.8125rem',
                                      fontWeight: 600
                                    }}>
                                      <Box sx={{ width: '30%', textAlign: 'left' }}>Year</Box>
                                      <Box sx={{ width: '25%', textAlign: 'center' }}>Count</Box>
                                      <Box sx={{ width: '45%', textAlign: 'right' }}>Matched On</Box>
                                    </Box>
                                    
                                    {/* Scrollable Content */}
                                    <Box sx={{ 
                                      height: 200,
                                      overflowY: 'auto',
                                      border: `1px solid ${theme.palette.grey[300]}`,
                                      borderBottomLeftRadius: '4px',
                                      borderBottomRightRadius: '4px',
                                      bgcolor: 'white',
                                      p: 1
                                    }}>
                                      {thresholdDays.length > 0 ? (
                                        <Table sx={{ 
                                          width: '100%', 
                                          tableLayout: 'fixed',
                                          '& td': { 
                                            border: 'none', 
                                            padding: '6px 6px',
                                            lineHeight: 1.1,
                                            fontSize: '0.8125rem',
                                            borderBottom: `1px solid ${theme.palette.grey[200]}`
                                          },
                                          '& tbody tr:last-child td': {
                                            borderBottom: 'none'
                                          }
                                        }}>
                                          <TableBody>
                                            {thresholdDays.map((item: any, idx: number) => (
                                              <TableRow key={idx}>
                                                <TableCell align="left" sx={{ width: '30%' }}>
                                                  {item.label}
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: '25%' }}>
                                                  {item.value !== null ? Math.round(item.value).toLocaleString() : ''}
                                                </TableCell>
                                                <TableCell align="right" sx={{ width: '45%' }}>
                                                  <Box sx={{ pr: 3 }}>
                                                    {item.day === 'Not reached' ? 'None' : item.day}
                                                  </Box>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      ) : (
                                        <Box sx={{ p: 1, textAlign: 'center', color: theme.palette.grey[500], fontSize: '0.75rem' }}>
                                          {currentThreshold === undefined ? 'Enter a threshold to filter' : 'No data'}
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                    </Box>

                    {/* MobileStepper with dots */}
                    <MobileStepper
                      variant="dots"
                      steps={maxSteps}
                      position="static"
                      activeStep={activeCarouselStep}
                      sx={{ bgcolor: 'transparent', justifyContent: 'center' }}
                      nextButton={<span />}
                      backButton={<span />}
                    />
                  </Box>
                );
              })()}

              {/* Date Limiters Editor */}
              {limitersLocal && limitersLocal.rows && limitersLocal.columns && !isProcessing && (
                <Box sx={{ flex: 'none', mt: 2, p: 2, bgcolor: 'white', border: theme => `1px solid ${theme.palette.grey[300]}`, borderRadius: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Set Date Limiters</Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={isProcessing || !hasLimiterChanges(limitersLocal, limitersDefault)}
                        sx={(theme) => ({
                          '&.Mui-disabled': {
                            opacity: 1,
                            color: theme.palette.grey[700],
                            backgroundColor: theme.palette.grey[300],
                            boxShadow: 'none'
                          }
                        })}
                        onClick={handleSaveAndRecalculate}
                      >
                        Save & Recalculate
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={resetLimiters}
                        disabled={!hasLimiterChanges(limitersLocal, limitersDefault)}
                        sx={(theme) => ({
                          '&.Mui-disabled': {
                            opacity: 1,
                            color: theme.palette.grey[500],
                            borderColor: theme.palette.grey[400],
                            backgroundColor: 'transparent'
                          }
                        })}
                      >
                        Reset
                      </Button>
                      {/* <Button variant="text" color="primary">Cancel</Button> */}
                    </Stack>
                  </Stack>
                  <TableContainer>
                    <Table size="small" sx={{
                      '& td, & th': { border: theme => `1px solid ${theme.palette.grey[300]}` },
                      '& thead th': { bgcolor: theme => theme.palette.grey[200] },
                    }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Year</TableCell>
                          {(limitersLocal.columns ?? []).map(col => (
                            <TableCell key={col.id} sx={{ fontWeight: 600 }}>{col.label}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(limitersLocal.rows ?? []).map(row => (
                          <TableRow key={row.year}>
                            <TableCell sx={{ fontWeight: 600 }}>{row.year}</TableCell>
                            {(limitersLocal.columns ?? []).map(col => {
                              const ymd = row.dates?.[col.id] || '';
                              const key = `${row.year}|${col.id}`;
                              const isoCommitted = ymd && ymd.length === 8 ? `${ymd.substring(0,4)}-${ymd.substring(4,6)}-${ymd.substring(6,8)}` : '';
                              const defaultYmd = limitersDefault?.rows?.find(r => r.year === row.year)?.dates?.[col.id] || '';
                              const isChanged = ymd !== defaultYmd;
                              return (
                                <TableCell key={`${row.year}-${col.id}`} align='center' sx={{ p: "4px 4px" }}>
                                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                      format="YYYY-MM-DD"
                                      value={isoCommitted ? dayjs(isoCommitted) : null}
                                      onChange={(v) => handleDateChange(row.year, col.id, v)}
                                      reduceAnimations
                                      slotProps={{
                                        popper: {
                                          disablePortal: false,
                                          placement: 'bottom-start',
                                          modifiers: [
                                            { name: 'flip', enabled: true, options: { fallbackPlacements: ['top-start', 'bottom-start'] } },
                                            { name: 'preventOverflow', options: { altBoundary: true, tether: true, rootBoundary: 'viewport' } },
                                          ],
                                          sx: { zIndex: (theme: any) => theme.zIndex.modal + 1 }
                                        },
                                        textField: {
                                          size: 'small',
                                          sx: {
                                            width: 100,
                                            '& .MuiInputBase-root': {
                                              paddingRight: 0
                                            },
                                            '& .MuiInputBase-input': {
                                              fontSize: '0.75rem',
                                              lineHeight: 1.15,
                                              letterSpacing: 0,
                                              padding: '3px 6px',
                                              backgroundColor: isChanged ? '#eeeeee' : 'white'
                                            },
                                            '& .MuiInputAdornment-root': { m: 0 },
                                            '& .MuiInputAdornment-positionEnd': { ml: '2px' },
                                            '& .MuiIconButton-root': { p: .5, m: 0 },
                                            '& .MuiSvgIcon-root': { fontSize: '0.8rem' }
                                          },
                                          // Block manual entry
                                          inputProps: { readOnly: true },
                                          onKeyDown: (e) => { e.preventDefault(); },
                                          onPaste: (e) => { e.preventDefault(); },
                                          onDrop: (e) => { e.preventDefault(); },
                                        },
                                        openPickerButton: {
                                          sx: { p: 0.125, m: 0 }
                                        }
                                      }}
                                    />
                                  </LocalizationProvider>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Message Alert - Date Limiters only available if current snapshot is also active */}
              { activeSnapshot && 
                (activeSnapshot.client_id !== props.snapshot.client_id || 
                activeSnapshot.academic_year !== props.snapshot.academic_year ||
                activeSnapshot.snapshot_date !== props.snapshot.snapshot_date) && (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Alert severity='info'>Date Limiters are available only for the currently active snapshot.</Alert>
                </Box>
              )} 

            </Box>
          </Box>
        </CustomTabPanel>    

        {/* Data Quality Reports Content Section */}
        <CustomTabPanel value={tabValue} index={1}>            
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, p:0 }}>
            {isDataQualityDownloading && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 5 }}>
                <CircularProgress />
              </Box>
            )}
            {dqReport && (
              <Box sx={{ flex: 'none', backgroundColor: 'white', pt: 2, pb: 2 }}>
                <Stack direction='row' spacing={2} sx={{ flexWrap: 'wrap' }}>
                  <Typography variant='caption'><strong>Record count:</strong> {dqReport.total_rows}</Typography>
                  <Typography variant='caption'><strong>Rule count:</strong> {dqReport.rules_evaluated}</Typography>
                  <Typography variant='caption'><strong>Generated:</strong> {formatTimestampMinutePrecision(dqReport.generated_at)}</Typography>
                </Stack>
              </Box>
            )}
            <Box sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              overflow: 'auto'
            }}>
              {dqReport && (
                <DataGridPro
                  columns={dataQualityColumns}
                  rows={wrapDQResults(dqReport.rule_summaries)}
                  rowThreshold={0}
                  disableDensitySelector={true}
                  disableColumnResize={false}
                  disableColumnSelector={true}
                  disableRowSelectionOnClick={true}
                  disableMultipleRowSelection={true}
                  getDetailPanelContent={getDetailPanelContent}
                  getDetailPanelHeight={getDetailPanelHeight}
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'pass_pct', sort: 'desc' }]
                    }
                  }}
                  slots={{ 
                    toolbar: GridToolbar,
                    footer: () => CustomFooter(dqReport.rule_summaries.length)
                  }}
                  slotProps={{
                    toolbar: {
                      sx: { backgroundColor: 'white' },
                      showQuickFilter: true,
                      printOptions: { disableToolbarButton: true },
                      csvOptions: {disableToolbarButton: true}
                    }}}
                  getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'
                  }
                  sx={{
                    flex: 1,
                    width: '100%',
                    '& .MuiDataGrid-root': {
                      color: theme => theme.palette.text.primary,
                      fontSize: '0.8rem'
                    },
                    '& .MuiDataGrid-columnHeader': {
                      backgroundColor: theme => theme.palette.background.paper,
                      color: theme => theme.palette.text.primary,
                      fontSize: '.8rem'
                    },
                    '& .MuiDataGrid-cell': {
                      backgroundColor: 'white',
                      color: theme => theme.palette.text.primary,
                      fontSize: '0.8rem'
                    }
                  }}
                />
              )}
              {!dqReport && !isDataQualityDownloading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                  <Alert severity='info'>Silver Data Quality Report has not yet been generated.</Alert>
                </Box>
              )}
            </Box>
          </Box>
        </CustomTabPanel>

        {/* Warnings/Errors Content Section */}
        <CustomTabPanel value={tabValue} index={2}>
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, p: 0 }}>
            {warnings && warnings.length > 0 && (
              <Box sx={{ flex: 'none', backgroundColor: 'white', pt: 2, pb: 2 }}>
                <Stack direction='row' spacing={2} sx={{ flexWrap: 'wrap' }}>
                  <Typography variant='caption'><strong>Warning count:</strong> {warnings.filter(w => w.type === 'warning').length}</Typography>
                  <Typography variant='caption'><strong>Error count:</strong> {warnings.filter(w => w.type === 'error').length}</Typography>
                </Stack>
              </Box>
            )}
            <Box sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              overflow: 'auto',
              scrollbarColor: theme => `${theme.palette.primary.main} ${theme.palette.grey[200]}`,
              '&::-webkit-scrollbar': { width: 10, height: 10 },
              '&::-webkit-scrollbar-track': { backgroundColor: theme => theme.palette.grey[200], borderRadius: 8 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: theme => theme.palette.primary.main, borderRadius: 8 },
              '&::-webkit-scrollbar-thumb:hover': { backgroundColor: theme => theme.palette.primary.dark },
            }}>
              {warnings && warnings.length > 0 && (
                <DataGridPro
                  columns={warningsColumns}
                  rows={warnings}
                  rowThreshold={0}
                  disableDensitySelector={true}
                  disableColumnResize={false}
                  disableColumnSelector={true}
                  disableRowSelectionOnClick={true}
                  disableMultipleRowSelection={true}
                  slots={{
                    toolbar: GridToolbar,
                    footer: () => CustomFooter(warnings.length)
                  }}
                  slotProps={{
                    toolbar: {
                      sx: { backgroundColor: 'white' },
                      showQuickFilter: true,
                      printOptions: { disableToolbarButton: true },
                      csvOptions: { disableToolbarButton: true }
                    }
                  }}
                  getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'
                  }
                  sx={{
                    flex: 1,
                    width: '100%',
                    '& .MuiDataGrid-root': {
                      color: theme => theme.palette.text.primary,
                      fontSize: '0.8rem'
                    },
                    '& .MuiDataGrid-columnHeader': {
                      backgroundColor: theme => theme.palette.background.paper,
                      color: theme => theme.palette.text.primary,
                      fontSize: '.8rem'
                    },
                    '& .MuiDataGrid-cell': {
                      backgroundColor: 'white',
                      color: theme => theme.palette.text.primary,
                      fontSize: '0.8rem'
                    }
                  }}
                />
              )}
              {warnings && warnings.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                  <Alert severity='info'>No warnings or errors for this snapshot.</Alert>
                </Box>
              )}
              {!warnings && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                  <Alert severity='info'>Warnings/errors not available.</Alert>
                </Box>
              )}
            </Box>
          </Box>
        </CustomTabPanel>

        {/* Log Files Content Section */}
        <CustomTabPanel value={tabValue} index={3}>
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, p: 0 }}>
            {isLogsDownloading && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 5 }}>
                <CircularProgress />
              </Box>
            )}
            {logReports && logReports.length > 0 && (
              <Box sx={{ flex: 'none', backgroundColor: 'white', pt: 2, pb: 2 }}>
                <Stack direction='row' spacing={2} sx={{ flexWrap: 'wrap' }}>
                  {logReports[0].generated_at && (
                    <Typography variant='caption'><strong>Generated:</strong> {formatTimestampMinutePrecision(logReports[0].generated_at)}</Typography>
                  )}
                </Stack>
              </Box>
            )}
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
              {logReports && logReports.length > 0 && (
                <DataGridPro
                  columns={logColumns}
                  rows={wrapLogset(logReports[0].logEntries)}
                  rowThreshold={0}
                  disableDensitySelector={true}
                  disableColumnResize={false}
                  disableColumnSelector={true}
                  disableRowSelectionOnClick={true}
                  disableMultipleRowSelection={true}
                  slots={{
                    toolbar: GridToolbar,
                    footer: () => CustomFooter(logReports[0].logEntries.length)
                  }}
                  slotProps={{
                    toolbar: {
                      sx: { backgroundColor: 'white' },
                      showQuickFilter: true,
                      printOptions: { disableToolbarButton: true },
                      csvOptions: { disableToolbarButton: true }
                    }
                  }}
                  getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'
                  }
                  sx={{
                    flex: 1,
                    width: '100%',
                    '& .MuiDataGrid-root': {
                      color: theme => theme.palette.text.primary,
                      fontSize: '0.8rem'
                    },
                    '& .MuiDataGrid-columnHeader': {
                      backgroundColor: theme => theme.palette.background.paper,
                      color: theme => theme.palette.text.primary,
                      fontSize: '.8rem'
                    },
                    '& .MuiDataGrid-cell': {
                      backgroundColor: 'white',
                      color: theme => theme.palette.text.primary,
                      fontSize: '0.8rem'
                    }
                  }}
                />
              )}
              {logReports && logReports.length === 0 && !isLogsDownloading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                  <Alert severity='info'>Silver Logs have not yet been generated.</Alert>
                </Box>
              )}
              {!logReports && !isLogsDownloading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                  <Alert severity='info'>Log report not available.</Alert>
                </Box>
              )}
            </Box>
          </Box>
        </CustomTabPanel>

      </Box>

      {/* Footer buttons */}
      <Box sx={{ flex: 'none', display: 'flex', justifyContent: 'center', pt: 2, pb: 0 }}>
        <Button variant="contained" color="primary" onClick={props.closeModal} disabled={isProcessing}>Close</Button>
      </Box>

      {/* Error modal */}
      <Dialog open={errorOpen} onClose={() => { setErrorOpen(false); props.closeModal(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{errorMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setErrorOpen(false); props.closeModal(); }} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, .05)'
      : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

const wrapDQResults = (results: Array<AmaV2DQRuleSummary>) => {
  let idCounter = 1;
  return results.map((jsonObject) => {
    const newObj = { ...jsonObject, id: idCounter };
    idCounter++;
    return newObj;
  });
};

const wrapLogset = (results: Array<string>) => {
  let idCounter = 1;
  return results.map((logEntry) => {
    const newObj = { logEntry, id: idCounter };
    idCounter++;
    return newObj;
  });
};


